import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import { useTranslation } from 'react-i18next';
import { translateApiError } from '../../../i18n/translateApiError';

import {
  ApiError,
  organizationApi,
  type Schema,
} from '../../../api';
import OrganizationFlow, {
  type OrganizationFlowNode,
} from '../../../components/organization/OrganizationFlow';

type OrgChartNode = Schema<'OrgChartNode'>;

function countSubtree(node: OrgChartNode): number {
  return 1 + (node.children ?? []).reduce((sum, child) => sum + countSubtree(child), 0);
}

function maxDepth(node: OrgChartNode): number {
  const children = node.children ?? [];
  return children.length === 0 ? 1 : 1 + Math.max(...children.map(maxDepth));
}

function toFlowNode(node: OrgChartNode): OrganizationFlowNode {
  return {
    id: node.id,
    name: node.name,
    title: node.title,
    department: node.department,
    children: (node.children ?? []).map(toFlowNode),
  };
}

const OrganizationChart: React.FC = () => {
  const { t } = useTranslation();
  const [roots, setRoots] = useState<OrgChartNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const fetchChart = useCallback(async () => {
    setLoading(true);
    try {
      setRoots(await organizationApi.orgChartApi.get());
      setError(null);
    } catch (e) {
      setError(
        e instanceof ApiError
          ? translateApiError(e, t)
          : t('org:organizationChart.toast.loadFailed'),
      );
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchChart();
  }, [fetchChart]);

  const stats = useMemo(() => {
    const total = roots.reduce((sum, root) => sum + countSubtree(root), 0);
    const depth = roots.length ? Math.max(...roots.map(maxDepth)) : 0;
    return { rootCount: roots.length, total, depth };
  }, [roots]);

  const flowRoots = useMemo(() => roots.map(toFlowNode), [roots]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        py: '12px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', md: 'center' },
          mb: 1.5,
          gap: 1.5,
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
            {t('org:organizationChart.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('org:organizationChart.subtitle')}
          </Typography>
        </Box>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <TextField
            size="small"
            placeholder={t('org:organizationChart.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 260,
              '& .MuiOutlinedInput-root': {
                borderRadius: 1,
                fontSize: '0.85rem',
                height: 32,
              },
            }}
          />
          <Tooltip title={t('common:actions.refresh')}>
            <IconButton
              size="small"
              onClick={fetchChart}
              aria-label={t('common:actions.refresh')}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                '&:hover': { borderColor: 'text.secondary' },
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>

      {!loading && roots.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <strong>{stats.rootCount}</strong>{' '}
          {stats.rootCount === 1
            ? t('org:organizationChart.stats.topLevelOne')
            : t('org:organizationChart.stats.topLevelOther')}
          {' · '}<strong>{stats.total}</strong> {t('org:organizationChart.stats.workers')}
          {' · '}<strong>{stats.depth}</strong>{' '}
          {stats.depth === 1
            ? t('org:organizationChart.stats.levelOne')
            : t('org:organizationChart.stats.levelOther')}
        </Typography>
      )}

      {loading && roots.length === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8, width: '100%' }}>
          <CircularProgress size={28} />
        </Box>
      )}

      {error && roots.length === 0 && !loading && (
        <Alert
          severity="error"
          sx={{ width: '100%' }}
          action={(
            <Button color="inherit" size="small" onClick={fetchChart}>
              {t('common:actions.retry')}
            </Button>
          )}
        >
          {error}
        </Alert>
      )}

      {roots.length === 0 && !loading && !error && (
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            p: 5,
            textAlign: 'center',
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('org:organizationChart.empty.title')}
          </Typography>
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 0.5 }}>
            {t('org:organizationChart.empty.body')}
          </Typography>
        </Paper>
      )}

      {roots.length > 0 && (
        <OrganizationFlow
          roots={flowRoots}
          search={search}
          ariaLabel={t('org:organizationChart.title')}
          height={560}
        />
      )}
    </Box>
  );
};

export default OrganizationChart;
